import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CircleCheck,
  ExternalLink,
  Handshake,
  ArrowLeft,
  Copy,
  Facebook,
  Linkedin,
  Instagram,
  MessageCircle,
  X as Close,
} from 'lucide-react';

import { fetchProductById } from '@/services/ProductService';
import type { Product, MediaDto } from '@/Data/products';

import FillerModal from '@/components/Fillermodal';

export default function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Extract numeric ID from slug
  const productId = useMemo(() => {
    if (!slug) return null;
    const id = parseInt(slug.split('-').pop() || '', 10);
    return isNaN(id) ? null : id;
  }, [slug]);

  // ============================================
  // FETCHED PRODUCT STATES
  // ============================================
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI Modals
  const [isFillerOpen, setIsFillerOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);

  const shareUrl = window.location.href;

  // ============================================
  // LOAD PRODUCT FROM API
  // ============================================
  useEffect(() => {
    if (!productId) {
      setError('Invalid product.');
      setLoading(false);
      return;
    }

    async function load() {
      try {
        if (productId === null) {
          setError('Invalid product ID');
          setLoading(false);
          return;
        }

        const p = await fetchProductById(productId);
        if (!p) {
          setError('Product not found');
        } else {
          setProduct(p);
        }
      } catch {
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [productId]);

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(shareUrl);
    alert('Link copied to clipboard!');
  };

  const openShareLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // ============================================
  // LOADING / ERROR
  // ============================================
  if (loading)
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="text-2xl font-bold">Loading...</h1>
      </section>
    );

  if (error || !product)
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="text-2xl font-bold">Product not found</h1>
        <p className="mt-2 text-gray-600">{error}</p>
        <button
          onClick={() => navigate('/')}
          className="mt-6 rounded-md border border-gray-800 bg-black px-5 py-2.5 text-sm font-semibold text-white"
        >
          Go back
        </button>
      </section>
    );

  // ============================================
  // ====== MAPPED FIELDS TO MATCH OLD UI =======
  // ============================================

  // MAIN IMAGE
  const mainImage =
    product.thumbnails?.[0] ||
    product.mediaGallery.find((m) => m.fileType === 'image')?.fileUrl ||
    '/fallback/no-image.png';

  // MEDIA GALLERY (old UI expects m.src + m.type)
  const mappedMedia =
    product.mediaGallery?.map((m) => ({
      src: m.fileUrl,
      type: m.fileType,
    })) ?? [];

  // FEATURES → SPEC LIST
  const specs = product.features?.map((f) => f.featureText) ?? [];

  // DEMO VIDEO
  const demoVideo = product.productVideoUrl || null;

  // OTHER PHOTOS SECTION (old UI: product.otherPhotos)
  const otherPhotos = [
    ...product.thumbnails.slice(1),
    ...product.mediaGallery
      .filter((m) => m.fileType === 'image')
      .map((m) => m.fileUrl),
  ];

  return (
    <>
      <section className="bg-white px-4 py-20 sm:px-6 md:px-16 lg:px-32">
        <div className="mx-auto max-w-6xl">
          {/* BACK BUTTON */}
          <div data-aos="fade-down" className="my-10">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-md border border-gray-400 bg-white px-9 py-4 text-sm text-gray-500"
            >
              <ArrowLeft className="h-4 w-4" />
              Go back
            </button>
          </div>

          {/* TOP SECTION */}
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
            {/* LEFT SIDE */}
            <div data-aos="fade-left" className="order-2 md:order-1">
              <h1 className="text-2xl font-extrabold text-[#0B0B0B] sm:text-3xl lg:text-4xl">
                {product.name}
              </h1>

              <p className="mt-3 text-3xl font-bold text-[#0B0B0B]">
                ₱{' '}
                {product.price.toLocaleString('en-PH', {
                  minimumFractionDigits: 2,
                })}
              </p>

              <p className="mt-4 pr-1 text-sm font-medium leading-tight text-black">
                {product.description}
              </p>

              {specs.length > 0 && (
                <ul className="mt-5 space-y-3 pr-1 text-sm font-medium text-black">
                  {specs.map((s, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CircleCheck className="mt-0.5 h-4 w-4 text-[#FFBD59]" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-6 grid grid-cols-3 gap-3">
                <button
                  onClick={() => setIsShareOpen(true)}
                  className="col-span-1 flex items-center justify-center gap-2 rounded-md border border-black px-4 py-4"
                >
                  <ExternalLink className="h-6 w-6" />
                  Share
                </button>

                <button
                  onClick={() => setIsFillerOpen(true)}
                  className="col-span-2 flex items-center justify-center gap-2 rounded-md bg-[#FFBD59] px-4 py-4 font-semibold"
                >
                  <Handshake className="h-6 w-6" />
                  Connect with us
                </button>
              </div>
            </div>

            {/* RIGHT SIDE */}
            <div data-aos="fade-right" className="order-1 md:order-2">
              <div className="sticky top-24">
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <div className="aspect-[4/3] w-full bg-black/5">
                    <img
                      src={mainImage}
                      alt={product.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>

                {/* MEDIA STRIP */}
                {mappedMedia.length > 0 && (
                  <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
                    {mappedMedia.map((m, idx) =>
                      m.type === 'image' ? (
                        <img
                          key={idx}
                          src={m.src}
                          className="h-16 w-24 rounded-md object-cover"
                        />
                      ) : (
                        <video
                          key={idx}
                          src={m.src}
                          muted
                          className="h-16 w-24 rounded-md bg-black"
                        />
                      ),
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* DEMO VIDEO */}
          {demoVideo ? (
            <div className="mt-20">
              <div className="aspect-video w-full overflow-hidden rounded-xl border border-gray-300">
                <video src={demoVideo} controls className="h-full w-full" />
              </div>
            </div>
          ) : null}

          {/* OTHER PHOTOS */}
          {otherPhotos.length > 0 && (
            <div className="mt-20">
              <h2 className="mb-8 text-left text-2xl font-extrabold text-[#0B0B0B]">
                Other <span className="text-[#FFBD59]">Photos</span>
              </h2>

              <div className="space-y-12">
                {otherPhotos.map((src, idx) => (
                  <div key={idx} className="overflow-hidden rounded-xl">
                    <img src={src} className="h-auto w-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* SHARE MODAL */}
      {isShareOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setIsShareOpen(false)}
        >
          <div className="relative w-[90%] max-w-sm rounded-lg border border-[#1a1a1a] bg-[#0B0B0B] p-6 text-center">
            <button
              className="absolute right-3 top-3 text-gray-400"
              onClick={() => setIsShareOpen(false)}
            >
              <Close className="h-5 w-5" />
            </button>

            <h3 className="mb-4 text-lg font-bold text-white">
              Share this product
            </h3>

            <div className="flex justify-center gap-4 text-[#FFBD59]">
              <button
                onClick={() =>
                  openShareLink(
                    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                      shareUrl,
                    )}`,
                  )
                }
              >
                <Facebook size={28} />
              </button>

              <button
                onClick={() =>
                  openShareLink(
                    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                      shareUrl,
                    )}`,
                  )
                }
              >
                <Linkedin size={28} />
              </button>

              <button
                onClick={() =>
                  openShareLink(
                    `fb-messenger://share?link=${encodeURIComponent(shareUrl)}`,
                  )
                }
              >
                <MessageCircle size={28} />
              </button>

              <button onClick={handleCopyLink}>
                <Copy size={28} />
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* FILLER MODAL */}
      <FillerModal
        isOpen={isFillerOpen}
        onClose={() => setIsFillerOpen(false)}
        title="Interested in this product?"
        message="Our APPOINTMENT SYSTEM is under development. You can reach us directly at"
        email="aerocompproject@gmail.com"
        subject={`Inquiry about ${product.name}`}
        body={`Hi AeroComp Team,%0D%0A%0D%0AI'd like to know more about ${product.name}.`}
      />
    </>
  );
}
